import { FC, useEffect, useState } from 'react';
import MainLayout from '../../layouts/index'
import Grid from '../../components/Grid';
import Breadcrumbs from '../../components/Breadcrumbs';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { Page } from '../../types/common/Page.types';
import { fetchEntries } from '../../contentful';
import PageTitle from '../../components/PageTitle';
import { formatDate } from '../../utils/formatDate';
import ArticlePreview from '../../components/ArticlePreview';
import styles from '../../styles/Blog.module.scss';
import { Preview } from '../../components/ArticlePreview/ArticlePreview.types';
import HomeNewsletter from '../../components/Newsletter/HomeNewsletter';
import Button from '../../components/Button';
import { ButtonType } from '../../components/Button/Button.types';
import { ParsedUrlQuery } from 'querystring';
import { mapLocale } from '../../lib/locales';
import { useTranslations } from '../../hooks/useTranslations';
import { useRouter } from 'next/router';

interface BlogArticlePreview {
    title: string;
    slug: string;
    excerpt: any;
    createdDate: any;
    featuredImage: any;
    externalSource?: string;
}

interface BlogPageProps {
    body: Page,
    articles: BlogArticlePreview[];
    totalArticles: number;
}

const PAGE_SIZE = 9;
const FIRST_PAGE_SIZE = PAGE_SIZE + 1;

const BlogPage: FC<BlogPageProps> = ({ body: { title, description, head }, articles, totalArticles }) => {
    const [lastArticle, ...restArticles] = articles;
    const [amountOfLoadedArticles, setAmountOfLoadedArticles] = useState(articles.length);
    const [articlesToShow, setArticlesToShow] = useState(restArticles);
    const [disabledFetch, setDisabledFetch] = useState(false);
    const { t } = useTranslations();
    const { locale } = useRouter();

    useEffect(() => {
        const [, ...nextRestArticles] = articles;

        setAmountOfLoadedArticles(articles.length);
        setArticlesToShow(nextRestArticles);
        setDisabledFetch(false);
    }, [articles]);

    const shouldShowLoadMoreBtn = amountOfLoadedArticles < totalArticles;

    const fetchArticles = async () => {
        setDisabledFetch(true);
        const response = await fetch(`/api/blog/load?limit=${PAGE_SIZE}&skip=${amountOfLoadedArticles}&locale=${locale || 'pl'}`);
        const data = await response.json();

        setArticlesToShow([...articlesToShow, ...data]);
        setAmountOfLoadedArticles(amountOfLoadedArticles + data.length);
        setDisabledFetch(false);
    }

    return (
        <MainLayout head={head ? head.fields : {}} hideOverflow>
            <Grid>
                <Breadcrumbs />
                <PageTitle title={title} description={description} />
                <section className={styles.lastArticle}>
                    <ArticlePreview 
                        title={lastArticle.title}
                        slug={lastArticle.slug}
                        excerpt={lastArticle.excerpt}
                        createdDate={lastArticle.createdDate}
                        featuredImage={lastArticle.featuredImage}
                        preview={Preview.HORIZONTAL}
                        externalSource={lastArticle.externalSource}
                    />
                </section>
                <section className={styles.blogList}>
                    {articlesToShow.map((article) => <ArticlePreview 
                        key={`article${article.slug}`}
                        title={article.title}
                        slug={article.slug}
                        excerpt={article.excerpt}
                        createdDate={article.createdDate}
                        featuredImage={article.featuredImage}
                        preview={Preview.VERTICAL}
                        externalSource={article.externalSource}
                    />)}
                </section>
                <div className={styles.loadMoreBox}>
                    {shouldShowLoadMoreBtn && <Button.B 
                        label={t.ARTILES.LOAD_MORE}
                        pattern={ButtonType.SECONDARY}
                        action={fetchArticles}
                        disabled={disabledFetch}
                    />}
                </div>
                <section>
                    <HomeNewsletter />
                </section>
            </Grid>
        </MainLayout>
    )
}

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext<ParsedUrlQuery>) => {
    const res = await fetchEntries({
        content_type: 'page',
        'fields.slug': 'blog',
        include: 2,
        locale: mapLocale(context.locale),
    });

    const { getMarkdownArticlePreviews } = await import('../../lib/articles');
    const articlesRes = getMarkdownArticlePreviews({
        locale: context.locale || 'pl',
        skip: 0,
        limit: FIRST_PAGE_SIZE,
    });

    const body = await res.data
        .map(p => ({ ...p.fields }))
        .shift();

    const articles = await articlesRes.data.map(article => ({
        ...article,
        createdDate: formatDate({
            dateObject: article.createdDate,
            formatString: 'dd MMMM yyyy',
            locale: mapLocale(context.locale),
        }),
    }));

    if (!body) {
        return {
            notFound: true
        }
    }

    return {
        props: {
            body,
            articles,
            totalArticles: articlesRes.total,
        }
    }
}

export default BlogPage;
